/**
 * Tiện ích quản lý thời hạn lưu trữ video (chính sách 3 ngày)
 */

export const VIDEO_RETENTION_DAYS = 3;
export const VIDEO_RETENTION_MS = VIDEO_RETENTION_DAYS * 24 * 60 * 60 * 1000;

/**
 * Phân tích thông tin hạn lưu trữ từ Job, OutputAsset hoặc Project
 * @param {Object} target - RenderJob, OutputAsset hoặc Project
 * @returns {Object} { isExpired, expiresAt, formattedExpiry, remainingText, isUrgent }
 */
export function getVideoExpiryInfo(target = {}) {
  if (!target) {
    return {
      isExpired: false,
      expiresAt: null,
      formattedExpiry: '',
      remainingText: '',
      isUrgent: false
    };
  }

  // 1. Kiểm tra cờ hết hạn đã được server đánh dấu sẵn
  if (target.isExpired || target.isVideoExpired || target.outputAsset?.isExpired) {
    return {
      isExpired: true,
      expiresAt: target.expiresAt ? new Date(target.expiresAt) : null,
      formattedExpiry: target.expiresAt ? formatDateTime(new Date(target.expiresAt)) : '',
      remainingText: 'Đã hết hạn',
      isUrgent: false
    };
  }

  // 2. Tìm mốc thời gian bắt đầu tính hạn (ưu tiên completedAt -> outputAsset.createdAt -> createdAt)
  const baseTimeStr =
    target.completedAt ||
    target.outputAsset?.completedAt ||
    target.outputAsset?.createdAt ||
    target.videoCreatedAt ||
    target.updatedAt ||
    target.createdAt;

  if (!baseTimeStr && !target.expiresAt) {
    return {
      isExpired: false,
      expiresAt: null,
      formattedExpiry: '',
      remainingText: '',
      isUrgent: false
    };
  }

  // 3. Xác định thời điểm hết hạn
  let expiresAtDate;
  if (target.expiresAt || target.videoExpiresAt || target.outputAsset?.expiresAt) {
    expiresAtDate = new Date(target.expiresAt || target.videoExpiresAt || target.outputAsset?.expiresAt);
  } else {
    const baseDate = new Date(baseTimeStr);
    expiresAtDate = new Date(baseDate.getTime() + VIDEO_RETENTION_MS);
  }

  const now = Date.now();
  const diffMs = expiresAtDate.getTime() - now;

  if (isNaN(diffMs)) {
    return {
      isExpired: false,
      expiresAt: null,
      formattedExpiry: '',
      remainingText: '',
      isUrgent: false
    };
  }

  if (diffMs <= 0) {
    return {
      isExpired: true,
      expiresAt: expiresAtDate,
      formattedExpiry: formatDateTime(expiresAtDate),
      remainingText: 'Đã hết hạn',
      isUrgent: false
    };
  }

  // Tính thời gian còn lại
  const remainingDays = Math.floor(diffMs / (24 * 60 * 60 * 1000));
  const remainingHours = Math.floor((diffMs % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000));
  const remainingMinutes = Math.floor((diffMs % (60 * 60 * 1000)) / (60 * 1000));

  let remainingText = '';
  if (remainingDays >= 1) {
    remainingText = `Còn ${remainingDays} ngày ${remainingHours} giờ`;
  } else if (remainingHours >= 1) {
    remainingText = `Còn ${remainingHours} giờ ${remainingMinutes} phút`;
  } else {
    remainingText = `Còn ${Math.max(1, remainingMinutes)} phút`;
  }

  const isUrgent = diffMs < 24 * 60 * 60 * 1000; // Còn dưới 24h

  return {
    isExpired: false,
    expiresAt: expiresAtDate,
    formattedExpiry: formatDateTime(expiresAtDate),
    remainingText,
    isUrgent
  };
}

function formatDateTime(date) {
  if (!date || isNaN(date.getTime())) return '';
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${hours}:${minutes} ngày ${day}/${month}/${year}`;
}
